import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import B2BecLink from '@/components/B2BLink'
import { AlertIcon } from '@/components/Icons'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { selectShouldRenderServiceMenu } from '@/store/selectors/configSelector'
import { setSelectedMachineInfo } from '@/store/slices/serviceCaseSlice'
import { ASYNC_STATUS, SERVICE_ENTRY } from '@/libs/constants'
import { formatDate, getFormatType } from '@/libs/utils/dateUtils'
import { formatSerialNumber } from '@/libs/utils/formatSerialNumber'
import { linkGenerator } from '@/libs/utils/language'

import B2becButton from '../B2becButton'
import B2becTooltip from '../B2becTooltip'
import ImageWithFallBack from '../ImageWithFallback'
import B2becAvailableServiceButton from './B2becAvailableServiceButton'
import B2becExcludedMessage from './B2becExcludedMessage'
import styles from './B2becProductCard.module.scss'

interface B2becProductCardProps {
  orderDate?: string
  name?: string
  category?: string
  imgUrl?: string
  materialNumber?: string
  serialNumber?: string
}

const B2becProductCard = ({
  orderDate,
  name,
  category,
  imgUrl,
  materialNumber,
  serialNumber,
}: B2becProductCardProps) => {
  const { t } = useTranslation()
  const [isExcludedMachine, setIsExcludedMachine] = useState(false)
  const [isShowMessage, setIsShowMessage] = useState(false)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const shouldRenderServiceMenu = useAppSelector(selectShouldRenderServiceMenu)

  const handleValidateSuccess = useCallback(() => {
    dispatch(
      setSelectedMachineInfo({
        id: materialNumber,
        serialNumber: serialNumber,
        isExcludedMachine: false,
        validateStatus: ASYNC_STATUS.SUCCESS,
        serviceEntry: SERVICE_ENTRY.MACHINE,
        faqList: [],
      }),
    )

    navigate(linkGenerator('/request-services'))
  }, [dispatch, navigate, materialNumber, serialNumber])

  const handleOpenMatchingProduct = useCallback(() => {
    navigate(linkGenerator(`/product/${materialNumber}/all`))
  }, [navigate, materialNumber])

  return (
    <div className={styles.container}>
      <div className={styles.cardBody}>
        {isExcludedMachine && isShowMessage && <B2becExcludedMessage />}
        <span className={styles.orderDate}>
          {formatDate(orderDate, getFormatType().default)}
        </span>
        <B2BecLink to={`/product/${materialNumber}`}>
          <ImageWithFallBack alt={name} height={198} width={198} src={imgUrl} />
        </B2BecLink>
        <h4 className={styles.categoryName}>{category}</h4>
        <h3 className={styles.cardItemTitle}>
          <B2BecLink
            className="link-normalize"
            to={`/product/${materialNumber}`}
          >
            <B2becTooltip content={name} title={name} />
          </B2BecLink>
        </h3>
        <div className="card-item__material-number--wrapper">
          <div className="card-item__item-number--wrapper">
            <B2BecLink
              className="link-normalize fadeIn-1"
              to={`/product/${materialNumber}`}
            >
              <span className={styles.cardItemNumber}>
                {`${t('productCard.itemNumber')} `}
                {materialNumber}
              </span>
            </B2BecLink>
            {isExcludedMachine && (
              <button
                className={styles.toggleMessageButton}
                type="button"
                onClick={() => setIsShowMessage((isShow) => !isShow)}
              >
                <AlertIcon />
              </button>
            )}
          </div>
          <p>
            {serialNumber && (
              <>
                {`${t('productCard.serialNumber')} `}
                {formatSerialNumber(serialNumber)}
              </>
            )}
          </p>
        </div>
        <div className={styles.cardButtonWrapper}>
          <B2becButton onClick={handleOpenMatchingProduct}>
            {t('myMachines.matchingProduct')}
          </B2becButton>
          {shouldRenderServiceMenu && (
            <B2becAvailableServiceButton
              materialNumber={materialNumber}
              onError={() => setIsExcludedMachine(true)}
              onSuccess={handleValidateSuccess}
            >
              {t('service.selectMachine.availableService')}
            </B2becAvailableServiceButton>
          )}
        </div>
      </div>
    </div>
  )
}

export default B2becProductCard
